import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import FileUpload from '@alefeducation/file-upload';
import { ThemeProvider as UILibraryThemeProvider } from '@alefeducation/ui-elements-library-core/lib/ThemeProvider';
import {
  FormControl,
  FormLabel,
  TextArea,
} from '@alefeducation/ui-elements-library-core';
import useLDClientWrapper from '../../featureToggles/useLDClientWrapper';
import Input from '../common/Input/Input';
import Layout from '../common/Layout/Layout';
import Container from '../common/Container/Container';
import Tooltip from '../common/Tooltip/Tooltip';
import {
  errorTypes,
  TASK_STATUS,
  uploadTypes,
} from '../common/FileUpload/constants';
import { Column, GridContainer, Row } from '../common/GridLayout/GridLayout';
import { getLangCode } from '../../utils/language';
import uploadResultToAttachmentInput from '../../utils/uploadResultToAttachmentInput';
import FeatureFlags from '../../featureToggles/FeatureFlags';
import AssignmentSection from './AssignmentSection';
import { FormRow, Required, Title, UploaderWrapper } from './style';
import CharacterCounter from './CharacterCounter';
import AssignmentModal from './AssignmentModal';
import AttachedFile from './AttachedFile';
import { assignmentDefaultProps, assignmentPropTypes } from './AssignmentProps';
import { AssignmentSettings } from './AssignmentSettings';
import useUploaderState from './useUploaderState';

const characterLimit = 20;
const dateFormat = 'DD-MM-YYYY';
const buttonTooltipText = `Check if all fields are filled`;

const toISOFormat = date =>
  moment(date, dateFormat).format('YYYY-MM-DDT23:59:59.999'); // set time to end of day

const parseMaxGradeForMutation = grade => {
  if (!grade) {
    return null;
  }
  return parseInt(grade, 10);
};

const isYes = opt => opt && opt.key === 'Yes';
const yesNoOptions = t => [
  {
    key: 'Yes',
    locale: {
      ar: t('Yes'),
    },
  },
  {
    key: 'No',
    locale: {
      ar: t('No'),
    },
  },
];
const getDefaultOption = (optionValue, t) =>
  optionValue ? yesNoOptions(t)[0] : yesNoOptions(t)[1];

const Assignment = props => {
  const { t, i18n } = useTranslation();
  const ldClient = useLDClientWrapper();
  const isAzureFileStorageEnabled = ldClient.variation(
    FeatureFlags.ENABLE_AZURE_FILE_STORAGE,
    false,
  );
  const [assignmentTitle, setAssignmentTitle] = useState(props.assignmentTitle);
  const [assignmentDescription, setAssignmentDescription] = useState(
    props.assignmentDescription,
  );
  const [assignmentMaxGrade, setAssignmentMaxGrade] = useState(
    props.assignmentMaxGrade,
  );
  const [assignmentMaxGradeError, setAssignmentMaxGradeError] = useState('');
  const [assignmentDueDate, setAssignmentDueDate] = useState(
    props.assignmentDueDate,
  );
  const [assignmentDueDateError, setAssignmentDueDateError] = useState('');

  const [isFormValid, setIsFormValid] = useState(false);
  const [buttonTooltip, setButtonTooltip] = useState(buttonTooltipText);
  const [isModalOpen, setModalOpen] = useState(false);
  const [dateForCalendar, setDateForCalendar] = useState({});
  const [gradeableOption, setGradeableOption] = useState(
    getDefaultOption(props.assignmentGradeable, t),
  );
  const [attachmentRequiredOption, setAttachmentRequiredOption] = useState(
    getDefaultOption(props.attachmentRequired, t),
  );
  const [commentRequiredOption, setCommentRequiredOption] = useState(
    getDefaultOption(props.commentRequired, t),
  );
  const calRef = useRef(null);

  const [isAssignmentCreationLoading, setAssignmentCreationLoading] = useState(
    false,
  );
  const [language, setLangauge] = useState({
    dir: i18n.dir(),
    lang: props.language,
    langCode: i18n.language,
  });
  const langCode = getLangCode(props.language);
  const allowableAssignmentMaxScore = 100;

  useEffect(
    () => {
      setLangauge({
        dir: i18n.dir(),
        lang: props.language,
        langCode: i18n.language,
      });
    },
    [i18n.language],
  );

  const handleGradeableOptionChange = selectedOption => {
    setAssignmentMaxGrade('');
    setGradeableOption(selectedOption);
  };
  const handleAttachmentRequiredOptionChange = selectedOption => {
    if (selectedOption.key === 'No')
      setCommentRequiredOption(getDefaultOption(true, t));
    else setCommentRequiredOption(getDefaultOption(false, t));
    setAttachmentRequiredOption(selectedOption);
  };
  const handleCommentRequiredOptionChange = selectedOption => {
    setCommentRequiredOption(selectedOption);
  };

  const {
    attachment,
    setAttachment,
    uploadError,
    selectedFileType,
    isUploading,
    setFileUploaderState,
  } = useUploaderState({
    initAttachment: props.attachment,
  });

  const isConversionError =
    uploadError && uploadError.errorType === errorTypes.conversionError;
  const conversionErrorAllowed = !(
    isConversionError && selectedFileType !== 'DOCUMENT'
  );
  const shouldShowAttachment =
    attachment && !isUploading && conversionErrorAllowed;

  const shouldShowFileUploader = () => {
    if (!attachment) {
      return true;
    }

    const hasTaskError =
      attachment.taskInfo &&
      attachment.taskInfo.status === TASK_STATUS.FAILED &&
      selectedFileType !== 'DOCUMENT';

    return isUploading || hasTaskError || !conversionErrorAllowed;
  };

  const validateFields = () => {
    let error = false;
    setAssignmentDueDateError('');
    setAssignmentMaxGradeError('');

    const date = moment(assignmentDueDate, dateFormat);

    if (!date.isSameOrAfter(undefined, 'day')) {
      setAssignmentDueDateError(
        t(
          'The due date cannot be less than the current date. Please change the due date.',
        ),
      );
      error = true;
    }
    if (!date.isValid()) {
      setAssignmentDueDateError(t('Invalid date.'));
      error = true;
    }
    if (!/^([0-9]{2})-([0-9]{2})-([0-9]{4})$/.test(assignmentDueDate)) {
      setAssignmentDueDateError(t('Invalid date format. Use DD-MM-YYYY'));
      error = true;
    }

    if (isYes(gradeableOption) && /[^0-9]/.test(assignmentMaxGrade)) {
      setAssignmentMaxGradeError(t('Please enter a number'));
      error = true;
    }

    if (isYes(gradeableOption) && parseInt(assignmentMaxGrade, 10) === 0) {
      setAssignmentMaxGradeError(t('Please enter Max Grade greater than 0'));
      error = true;
    }
    if (
      isYes(gradeableOption) &&
      parseInt(assignmentMaxGrade, 10) > allowableAssignmentMaxScore
    ) {
      setAssignmentMaxGradeError(t('Exceeds the limit of 100'));
      error = true;
    }

    if (
      !assignmentTitle.trim() ||
      !assignmentDescription.trim() ||
      (isYes(gradeableOption) && !assignmentMaxGrade) ||
      isUploading
    ) {
      error = true;
    }

    return !error;
  };

  const updateDateAction = value => {
    setAssignmentDueDate(value.replace(/[^0-9-]/g, ''));
  };

  useEffect(() => {
    const validateResult = validateFields();
    setIsFormValid(validateResult);
    if (validateResult) {
      setButtonTooltip('');
    } else {
      setButtonTooltip(buttonTooltipText);
    }
  });

  useEffect(
    () => {
      const dueDate = moment(assignmentDueDate, dateFormat);
      const validDueDate = dueDate.isValid() ? dueDate.toDate() : undefined;
      setDateForCalendar(validDueDate);
    },
    [assignmentDueDate],
  );

  useEffect(
    () => {
      if (props.assignmentTitle) {
        setAssignmentTitle(props.assignmentTitle);
        setAssignmentDescription(props.assignmentDescription);
        setAssignmentDueDate(props.assignmentDueDate);
        setAssignmentMaxGrade(props.assignmentMaxGrade);
        setAttachment(props.attachment);
      }
    },
    [props.assignmentTitle],
  );

  const toggleModal = () => setModalOpen(!isModalOpen);

  const goBackToPreviousPage = () => {
    props.onGoBack();
  };

  const {
    mloName,
    subjectCategory,
    classId,
    mloId,
    trimesterId,
    instructionalPlanId,
    teachingPeriodId,
  } = props.mloData;

  const assignmentMutation = async assignmentStatus => {
    const variables = {
      input: {
        title: assignmentTitle.trim(),
        description: assignmentDescription.trim(),
        maxScore: parseMaxGradeForMutation(assignmentMaxGrade),
        attachment: uploadResultToAttachmentInput(attachment),
        gradeable: !!isYes(gradeableOption),
        attachmentRequired: isYes(attachmentRequiredOption),
        commentRequired: isYes(commentRequiredOption),
        language: props.language,
        status: assignmentStatus,
        assignmentInstance: {
          dueOn: toISOFormat(assignmentDueDate),
          classId,
          mloId,
          trimesterId,
          teachingPeriodId,
          instructionalPlanId,
        },
      },
    };
    let mutationName = 'assignmentCreate';
    if (props.assignmentInstanceId) {
      variables.assignmentInstanceId = props.assignmentInstanceId;
      mutationName = 'assignmentUpdate';
      // TODO: Do not populate these values in first place
      delete variables.input.assignmentInstance.mloId;
      delete variables.input.assignmentInstance.trimesterId;
      delete variables.input.assignmentInstance.teachingPeriodId;
    }

    setAssignmentCreationLoading(true);
    if (assignmentStatus !== 'DRAFT') {
      toggleModal();
    }
    const response = await props.mutation({ variables });
    if (
      response &&
      response.data[mutationName] &&
      response.data[mutationName].assignmentInstance &&
      response.data[mutationName].assignmentInstance.id
    ) {
      goBackToPreviousPage();
    } else {
      setAssignmentCreationLoading(false);
    }
  };

  const setFileUploadingState = taskInfo => {
    setFileUploaderState(prevState => ({
      ...prevState,
      isUploading: taskInfo && taskInfo.status === TASK_STATUS.IN_PROGRESS,
    }));
  };
  return (
    <UILibraryThemeProvider>
      <Layout
        backButtonText={t('Back to Lesson')}
        title={mloName}
        subjectCategory={subjectCategory}
        onGoBack={goBackToPreviousPage}
      >
        <GridContainer>
          <Row>
            <Column colSpan="col-12">
              <Container>
                <Title>{props.title}</Title>
                <AssignmentSection title={t('Basic Information')}>
                  <FormRow>
                    <FormControl>
                      <FormLabel data-testid="assignment-title-input-label">
                        <Required>*</Required>
                        &nbsp;
                        {t('Assignment Title')}
                      </FormLabel>
                      <Input
                        onChange={e => setAssignmentTitle(e.target.value)}
                        value={assignmentTitle}
                        testId="assignment-title"
                        maxlength={characterLimit}
                      />
                      <CharacterCounter
                        characterLimit={characterLimit}
                        text={assignmentTitle}
                      />
                    </FormControl>
                  </FormRow>
                  <FormRow>
                    <FormControl>
                      <FormLabel
                        data-testid={'assignment-description-textarea-label'}
                      >
                        <Required>*</Required>
                        &nbsp;
                        {t('Description')}
                      </FormLabel>
                      <TextArea
                        placeholder={t('Put your text here')}
                        onChange={e => setAssignmentDescription(e.target.value)}
                        value={assignmentDescription}
                        data-testid="assignment-description-textarea"
                      />
                    </FormControl>
                  </FormRow>
                  <FormRow>
                    <UploaderWrapper>
                      {shouldShowFileUploader() && (
                        <FileUpload
                          configs={{
                            consumerId:
                              window.AssignmentAppConfig.fileService
                                .FILE_SERVICE_CONSUMER_ID,
                            uploadType: uploadTypes.attachment,
                            configCode:
                              window.AssignmentAppConfig.fileService
                                .ASSIGNMENT_CREATION_ATTACHMENT,
                            videoFileDurationMins:
                              window.AssignmentAppConfig
                                .VIDEO_FILE_DURATION_MINS,
                            taskInfoPollInterval:
                              window.AssignmentAppConfig
                                .TASK_INFO_POLL_INTERVAL,
                            taskInfoPollTimeout:
                              window.AssignmentAppConfig.TASK_INFO_POLL_TIMEOUT,
                            enableAzureFileStorage: isAzureFileStorageEnabled,
                          }}
                          onStart={fileType => {
                            setFileUploaderState(prevState => ({
                              ...prevState,
                              isUploading: true,
                              selectedFileType: fileType,
                            }));
                          }}
                          onRemove={setAttachment}
                          onComplete={uploadResult => {
                            setAttachment(uploadResult);

                            const taskInfo =
                              uploadResult && uploadResult.taskInfo;

                            setFileUploadingState(taskInfo);
                          }}
                          onTaskUpdate={taskInfo => {
                            setFileUploadingState(taskInfo);
                          }}
                          onError={error => {
                            // eslint-disable-next-line no-param-reassign
                            error.message = t(error.message);
                            setFileUploaderState(prevState => ({
                              ...prevState,
                              isUploading: false,
                              uploadError: error,
                            }));
                          }}
                          useAsHiddenInputFilePlaceholder={false}
                          language={language}
                        />
                      )}
                      {attachment &&
                        shouldShowAttachment && (
                          <AttachedFile
                            attachment={attachment}
                            removeAttachment={setAttachment}
                          />
                        )}
                    </UploaderWrapper>
                  </FormRow>
                </AssignmentSection>
                <AssignmentSettings
                  assignmentDueDate={assignmentDueDate}
                  assignmentDueDateError={assignmentDueDateError}
                  assignmentMaxGradeError={assignmentMaxGradeError}
                  assignmentMutation={assignmentMutation}
                  attachmentRequiredOption={attachmentRequiredOption}
                  buttonTooltip={buttonTooltip}
                  calRef={calRef}
                  commentRequiredOption={commentRequiredOption}
                  dateForCalendar={dateForCalendar}
                  goBackToPreviousPage={goBackToPreviousPage}
                  gradeableOption={gradeableOption}
                  handleAttachmentRequiredOptionChange={
                    handleAttachmentRequiredOptionChange
                  }
                  handleCommentRequiredOptionChange={
                    handleCommentRequiredOptionChange
                  }
                  handleGradeableOptionChange={handleGradeableOptionChange}
                  isFormValid={isFormValid}
                  isYes={isYes}
                  langCode={langCode}
                  setAssignmentMaxGrade={setAssignmentMaxGrade}
                  toggleModal={toggleModal}
                  updateDateAction={updateDateAction}
                  yesNoOptions={yesNoOptions(t)}
                  assignmentMaxGrade={assignmentMaxGrade}
                  onUpdateCalendar={value =>
                    setAssignmentDueDate(moment(value).format(dateFormat))
                  }
                />
              </Container>
            </Column>
          </Row>
        </GridContainer>
        <Tooltip />
        {isModalOpen && (
          <AssignmentModal
            toggleModal={toggleModal}
            assignmentMutation={assignmentMutation}
            loading={isAssignmentCreationLoading}
          />
        )}
      </Layout>
    </UILibraryThemeProvider>
  );
};

Assignment.defaultProps = assignmentDefaultProps;
Assignment.propTypes = assignmentPropTypes;

export default Assignment;
